import {Search} from "../Search";
import {Crit} from "./Crit";
import {Zp} from "../Zp";
import {GpsBoundStr, GpsPos, GpsPosStr} from "../maps/igps";

export interface Filter {
    crits: Crit[] // Suchkriterien als Liste mit den Bit-Positionen
    gps: {
        start: GpsPosStr // Startposition einer GPS-Suche format: fmt.Sprintf("%016x", uint64(Filter.GpsStart)) kann leer sein
        end: GpsPosStr   // Endposition einer GPS-Suche, leer = nicht gesetzt, negative Zahl: Umkreissuche als Meter-Distanz Radius
    }
    place_id: number      // gibt den Ort an (0 = nicht gesetzt)
    region_id: number     // gibt die Region an (0 = nicht gesetzt)
    country_id: number    // gibt das Land an (0 = nicht gesetzt)
    price_from_to: number // gibt den von und bis Preis an, (x & 0x3ff) = von, (x >> 10) = bis, jeweils 0 = nicht gesetzt
    stay_from_to: number  // gibt die Reisezeit an, (x & 0x3ff) == Übernachtungen, (x >> 10) = Anreise als date.Short, jeweils 0 = nicht gesetzt
}

export interface FilterExt {
    country_urls: { [key: number]: string }
    country_names: { [key: number]: string }
    country_gps: { [key: number]: GpsBoundStr }
    region_urls: { [key: number]: string }
    region_names: { [key: number]: string }
    region_gps: { [key: number]: GpsBoundStr }
    place_urls: { [key: number]: string }
    place_names: { [key: number]: string }
    place_gps: { [key: number]: GpsBoundStr }
    crit_urls: { [key: number]: string }
    crit_names: { [key: number]: string }
    active_crits: Crit[]
    month_urls: { [key: number]: string }
    txt: { [key: string]: string }
}

export function backupFilter(filter: Filter): string {
    return JSON.stringify(filter);
}

export function restoreFilter(filter: Filter, backupStr: string) {
    const tmp = JSON.parse(backupStr) as Filter;
    filter.crits = tmp.crits;
    filter.gps = tmp.gps;
    filter.place_id = tmp.place_id;
    filter.region_id = tmp.region_id;
    filter.country_id = tmp.country_id;
    filter.price_from_to = tmp.price_from_to;
    filter.stay_from_to = tmp.stay_from_to;
}

function removeSingleFilterCrit(filter: Filter, crit: Crit) {
    const index = filter.crits.indexOf(crit);
    if (index >= 0) filter.crits.splice(index, 1);
}

export function removeFilterCrit(filter: Filter, crit: Crit) {
    switch (crit) {
        case Crit.BedroomFrom1:
        case Crit.BedroomFrom2:
        case Crit.BedroomFrom3:
        case Crit.BedroomFrom4:
            removeSingleFilterCrit(filter, Crit.BedroomFrom1);
            removeSingleFilterCrit(filter, Crit.BedroomFrom2);
            removeSingleFilterCrit(filter, Crit.BedroomFrom3);
            removeSingleFilterCrit(filter, Crit.BedroomFrom4);
            break;
        case Crit.PersonsFrom2:
        case Crit.PersonsFrom3:
        case Crit.PersonsFrom4:
        case Crit.PersonsFrom5:
        case Crit.PersonsFrom6:
        case Crit.PersonsFrom7:
        case Crit.PersonsFrom8:
        case Crit.PersonsFrom9:
        case Crit.PersonsFrom10:
            removeSingleFilterCrit(filter, Crit.PersonsFrom2);
            removeSingleFilterCrit(filter, Crit.PersonsFrom3);
            removeSingleFilterCrit(filter, Crit.PersonsFrom4);
            removeSingleFilterCrit(filter, Crit.PersonsFrom5);
            removeSingleFilterCrit(filter, Crit.PersonsFrom6);
            removeSingleFilterCrit(filter, Crit.PersonsFrom7);
            removeSingleFilterCrit(filter, Crit.PersonsFrom8);
            removeSingleFilterCrit(filter, Crit.PersonsFrom9);
            removeSingleFilterCrit(filter, Crit.PersonsFrom10);
            break;
        case Crit.WaterTill250:
        case Crit.WaterTill500:
        case Crit.WaterTill1000:
        case Crit.WaterTill10000:
            removeSingleFilterCrit(filter, Crit.WaterTill250);
            removeSingleFilterCrit(filter, Crit.WaterTill500);
            removeSingleFilterCrit(filter, Crit.WaterTill1000);
            removeSingleFilterCrit(filter, Crit.WaterTill10000);
            break;
        case Crit.SkiliftTill250:
        case Crit.SkiliftTill500:
        case Crit.SkiliftTill1000:
        case Crit.SkiliftTill10000:
            removeSingleFilterCrit(filter, Crit.SkiliftTill250);
            removeSingleFilterCrit(filter, Crit.SkiliftTill500);
            removeSingleFilterCrit(filter, Crit.SkiliftTill1000);
            removeSingleFilterCrit(filter, Crit.SkiliftTill10000);
            break;
        case Crit.TypeHolidayhome:
        case Crit.TypeApartment:
            removeSingleFilterCrit(filter, Crit.TypeHolidayhome);
            removeSingleFilterCrit(filter, Crit.TypeApartment);
            break;
        default:
            removeSingleFilterCrit(filter, crit);
            break;
    }
}

export function addFilterCrit(filter: Filter, crit: Crit) {
    removeFilterCrit(filter, crit);
    filter.crits.push(crit);
}

declare global {
    interface Window {
        filterJson: Filter
        filterExtJson: FilterExt
    }
}

export function getFilterUrl(filter: Filter, pageNumber?: number) {
    const ext = Search.filterExt;

    let result = document.location.origin + "/";

    const fc = (crit: Crit) => filter.crits.indexOf(crit) >= 0;
    const fcs = (crits: Crit[]) => {
        for (let i = 0; i < crits.length; i++) {
            if (fc(crits[i])) {
                result += ext.crit_urls[crits[i]] + "/";
            }
        }
    };

    fcs([Crit.Ac, Crit.Dishwasher, Crit.Fireplace, Crit.Internet, Crit.Jacuzzi, Crit.Parking, Crit.Pets, Crit.Sauna, Crit.Pool, Crit.Tv, Crit.WashingMachine]);

    fcs([Crit.BedroomFrom1, Crit.BedroomFrom2, Crit.BedroomFrom3, Crit.BedroomFrom4]);

    if (filter.place_id) result += ext.place_urls[filter.place_id] + "/";
    if (filter.region_id) result += ext.region_urls[filter.region_id] + "/";
    if (filter.country_id) result += ext.country_urls[filter.country_id] + "/";

    fcs([Crit.PersonsFrom2, Crit.PersonsFrom3, Crit.PersonsFrom4, Crit.PersonsFrom5, Crit.PersonsFrom6, Crit.PersonsFrom7, Crit.PersonsFrom8, Crit.PersonsFrom9, Crit.PersonsFrom10]);

    fcs([Crit.WaterTill250, Crit.WaterTill500, Crit.WaterTill1000, Crit.WaterTill10000, Crit.SkiliftTill250, Crit.SkiliftTill500, Crit.SkiliftTill1000, Crit.SkiliftTill10000]);

    if (filter.price_from_to !== 0) {
        const from = filter.price_from_to & 0x3ff;
        const till = filter.price_from_to >> 10;
        if (till == 0) result += "price-from-" + from + "/";
        else if (from == 0) result += "price-till-" + till + "/";
        else result += "price-" + from + "-and-" + till + "/";
    }

    if (filter.stay_from_to !== 0) {
        const from = filter.stay_from_to >> 10;
        const nights = filter.stay_from_to & 0x3ff;
        const p = Zp.toParts(from);
        const two = (n: number) => (n / 10 | 0) + "" + n % 10;
        result += "arrival-" + two(p.day) + "-" + ext.month_urls[p.month] + "-" + p.year + "/" + nights + "-nights/";
    }

    fcs([Crit.LastMinute, Crit.TypeHolidayhome, Crit.TypeApartment]);

    if (pageNumber && pageNumber > 1) result += "page-" + pageNumber + "/";

    if (filter.gps.start.length == 16 && filter.gps.end.length == 16) {
        const s = new GpsPos(filter.gps.start);
        const e = new GpsPos(filter.gps.end);
        result += "?b=" + s.latStr() + "," + e.longStr() + "," + e.latStr() + "," + s.longStr();
    }

    return result;
}

export function getLocationName(filter: Filter): string {
    const ext = Search.filterExt;
    let n = ext.place_names[filter.place_id];
    if (n) return n;
    n = ext.region_names[filter.region_id];
    if (n) return n;
    n = ext.country_names[filter.country_id];
    return n ? n : "World";
}
